import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import Dropzone from "react-dropzone";
import useForceUpdate from "use-force-update";
import { object, string } from "yup";
import { InputErrorMessage, Loading } from "../ui";
import AutoComplete from "../ui/AutoComplete";
import { Payment } from "./Payment";

interface IProps {
	payment: Payment;
	submitDisabled: boolean;
	manualValidate?: number;
	showSubmit: boolean;
	onSubmit: (payment: Payment, files?: File[]) => void;
}

export const ProofOfPaymentForm = (props: IProps) => {
	const [files, setFiles] = useState<File[]>();
	const [errors, setErrors] = useState(null);
	const forceUpdate = useForceUpdate();

	const payment = props.payment;

	const paymentMethods = [
		"Bank Deposit",
		"Online Banking Transfer",
		"Cash",
		"Company Cheque",
	];

	useEffect(() => {
		if (props.manualValidate && props.manualValidate > 0) {
			validate();
		}
	}, [props.manualValidate]);

	if (!payment) {
		return <Loading />;
	}

	const validate = () => {
		setErrors(null);
		const schema = object({
			method: string().required("Please select a payment option."),
			currency: string().required(
				"Please indicate the payment currency.",
			),
			amount: string().required("Please indicate the payment amount."),
			bankName: string().when("method", {
				is: paymentMethods[0] || paymentMethods[1],
				then: string().required("Please enter your bank name."),
			}),
			bankBranch: string().when("method", {
				is: paymentMethods[0] || paymentMethods[1],
				then: string().required("Please enter your bank branch."),
			}),
			chequeNumber: string().when("method", {
				is: paymentMethods[3],
				then: string().required("Please enter your cheque number."),
			}),
		});

		const schemaObject = {
			method: payment.method,
			currency: payment.currency,
			amount: payment.amount,
			bankName: payment.bankName,
			bankBranch: payment.bankBranch,
			chequeNumber: payment.chequeNumber,
		};

		schema
			.validate(schemaObject, { abortEarly: false })
			.then(valid => {
				if (valid) {
					props.onSubmit(payment, files);
				}
			})
			.catch(err => {
				setErrors(err);
			});
	};

	const suggestionsOne = [
		{
			name: "JMD - Jamaican",
			currency: "JMD",
		},
		{
			currency: "USD",
			name: "USD - US Dollars",
		},
		{
			name: "CAD - Canadian Dollars",
			currency: "CAD",
		},
		{
			name: "GBP - UK Pound",
			currency: "GBP",
		},
	];

	return (
		<Row className="mb-3">
			<Col xs={12} sm={6}>
				<Form.Group controlId="method">
					<Form.Label>Method</Form.Label>
					<Form.Control
						as="select"
						size="lg"
						defaultValue={payment.method}
						onChange={(event: any) => {
							payment.method = event.target.value;
							forceUpdate();
						}}
					>
						<option>-Select-</option>
						{paymentMethods.map(method => (
							<option value={method}>{method}</option>
						))}
					</Form.Control>
					<InputErrorMessage errors={errors} field="method" />
				</Form.Group>
			</Col>
			<Col xs={12} sm={6}>
				<Form.Group controlId="currency">
					<Form.Label>Currency</Form.Label>
					<AutoComplete
						onChange={(value: any) => (payment.currency = value)}
						suggestions={suggestionsOne}
					/>
					<InputErrorMessage errors={errors} field="currency" />
				</Form.Group>
			</Col>
			<Col xs={12} sm={6}>
				<Form.Group controlId="amount">
					<Form.Label>Amount</Form.Label>
					<Form.Control
						size="lg"
						defaultValue={payment.amount}
						onChange={(event: any) => {
							payment.amount = event.target.value;
							forceUpdate();
						}}
					/>
					<InputErrorMessage errors={errors} field="amount" />
				</Form.Group>
			</Col>
			{payment.method === paymentMethods[0] ||
			payment.method === paymentMethods[1] ? (
				<>
					<Col xs={12} sm={6}>
						<Form.Group controlId="bankName">
							<Form.Label>Bank Name</Form.Label>
							<AutoComplete
								onChange={(value: any) => {
									payment.bankName = value;
									forceUpdate();
								}}
								suggestions={[
									{
										name: "Bank of Nova Scotia",
									},
									{
										name: "Jamaica National Bank",
									},
								]}
							/>

							<InputErrorMessage
								errors={errors}
								field="bankName"
							/>
						</Form.Group>
					</Col>
					<Col xs={12} sm={6}>
						<Form.Group controlId="bankBranch">
							<Form.Label>Bank Branch</Form.Label>
							<Form.Control
								size="lg"
								defaultValue={payment.bankBranch}
								onChange={(event: any) => {
									payment.bankBranch = event.target.value;
									forceUpdate();
								}}
							/>
							<InputErrorMessage
								errors={errors}
								field="bankBranch"
							/>
						</Form.Group>
					</Col>
				</>
			) : (
				""
			)}
			{payment.method === paymentMethods[3] ? (
				<Col xs={12} sm={6}>
					<Form.Group controlId="chequeNumber">
						<Form.Label>Cheque Number</Form.Label>
						<Form.Control
							size="lg"
							defaultValue={payment.chequeNumber}
							onChange={(event: any) => {
								payment.chequeNumber = event.target.value;
							}}
						/>
						<InputErrorMessage
							errors={errors}
							field="chequeNumber"
						/>
					</Form.Group>
				</Col>
			) : (
				""
			)}
			<Col sm={12} className="pt-4 pb-4">
				<Form.Label>Documents</Form.Label>

				<p className="help mt-2">
					Optionally Upload proof of payment. Proof of payment could
					be a bank receipt or proof of online payment and must
					include payment date, amount and the University of the
					Commonwealth Caribbean as a recipeent.
				</p>

				<Dropzone
					onDrop={async acceptedFiles => {
						setFiles(acceptedFiles);
						return acceptedFiles;
					}}
				>
					{({ getRootProps, getInputProps }) => (
						<section className="dropzone">
							<div {...getRootProps()}>
								<input {...getInputProps()} />
								Drag &amp; drop files here, or click to select
								files
							</div>
						</section>
					)}
				</Dropzone>
				<ul>
					{files
						? files.map((file, idx) => {
								return <li key={idx}>{file.name}</li>;
						  })
						: ""}
				</ul>
			</Col>
			{props.showSubmit ? (
				<Col sm={12}>
					<Button
						onClick={validate}
						variant="primary"
						disabled={props.submitDisabled}
					>
						Submit
					</Button>
				</Col>
			) : (
				""
			)}
		</Row>
	);
};
